import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

export const MEAL_PLAN_NOTES_MAX_LENGTH = 2000;

type MealPlanNotesControlProps = {
  value?: string | null;
  onSave?: (notes: string | null) => Promise<boolean>;
  disabled?: boolean;
  /** sm = meal rows; md = plan/day surfaces */
  size?: 'sm' | 'md';
  /** Short title shown on the note card and dialog, e.g. "Day note". */
  label?: string;
  /** Button text when there is no note yet. */
  emptyLabel?: string;
  /** One-line tip under the empty/add state / in the dialog. */
  helperText?: string;
  /** Placeholder inside the writing box. */
  placeholder?: string;
  /** When false, renders a read-only note preview (no add/edit). */
  editable?: boolean;
  /**
   * card = full empty/filled card (plan/day).
   * icon = compact icon button for meal headers (hover shows note, click opens dialog).
   */
  variant?: 'card' | 'icon';
  /** onDark = white icon for cyan meal headers. */
  tone?: 'default' | 'onDark';
  /** Optional short label beside the icon (toolbar use, e.g. "Plan note"). */
  iconLabel?: string;
};

function normalizeNotesForCompare(value: string | null | undefined): string {
  return String(value ?? '').trim();
}

function toPersistedNotes(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

/**
 * Practitioner notes for plan / day / meal levels.
 * card: large add/preview surfaces.
 * icon: meal-slot header icon with hover preview + dialog editor.
 */
export const MealPlanNotesControl: React.FC<MealPlanNotesControlProps> = ({
  value,
  onSave,
  disabled = false,
  size = 'md',
  label = 'Note',
  emptyLabel = 'Write a note',
  helperText = 'Optional. You can add reminders or tips for yourself or your client.',
  placeholder = 'Enter notes…',
  editable = true,
  variant = 'card',
  tone = 'default',
  iconLabel,
}) => {
  const persisted = normalizeNotesForCompare(value);
  const [isOpen, setIsOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(persisted);
  const [isSaving, setIsSaving] = React.useState(false);
  /** Snapshot of dialog chrome so parent `disabled`/value updates during save don't flip Cancel↔Close. */
  const [editorSession, setEditorSession] = React.useState<{
    mode: 'add' | 'edit' | 'view';
    hadNote: boolean;
  } | null>(null);
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);

  const isSm = size === 'sm';
  const hasNote = persisted.length > 0;
  const isDirty = toPersistedNotes(draft) !== (persisted.length ? persisted : null);
  const overLimit = draft.length > MEAL_PLAN_NOTES_MAX_LENGTH;
  // Keep edit capability tied to `editable` only — parent `disabled` is for triggers / busy state,
  // not for swapping the open dialog into a read-only "Close" view mid-save.
  const canInteract = editable;
  const triggerDisabled = disabled || isSaving;
  const isIcon = variant === 'icon';
  const isOnDark = tone === 'onDark';
  const sessionMode = editorSession?.mode ?? (canInteract ? (hasNote ? 'edit' : 'add') : 'view');
  const sessionHadNote = editorSession?.hadNote ?? hasNote;

  React.useEffect(() => {
    if (!isOpen) setDraft(persisted);
  }, [isOpen, persisted]);

  React.useEffect(() => {
    if (!isOpen || !canInteract) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [isOpen, canInteract]);

  const openEditor = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (!canInteract && !hasNote) return;
    if (canInteract && disabled) return;
    setDraft(persisted);
    setEditorSession({
      mode: canInteract ? (hasNote ? 'edit' : 'add') : 'view',
      hadNote: hasNote,
    });
    setIsOpen(true);
  };

  const closeEditor = () => {
    if (isSaving) return;
    setIsOpen(false);
    setEditorSession(null);
    setDraft(persisted);
  };

  const handleSave = async (nextValue?: string) => {
    if (!canInteract || !onSave || isSaving) return;
    const raw = nextValue ?? draft;
    if (raw.length > MEAL_PLAN_NOTES_MAX_LENGTH) return;
    const nextNotes = toPersistedNotes(raw);
    if (nextNotes === (persisted.length ? persisted : null)) {
      setIsOpen(false);
      setEditorSession(null);
      return;
    }
    setIsSaving(true);
    try {
      const ok = await onSave(nextNotes);
      if (ok) {
        setDraft(nextNotes ?? '');
        setIsOpen(false);
        setEditorSession(null);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this note? This cannot be undone.')) return;
    await handleSave('');
  };

  if (isIcon && !canInteract && !hasNote) {
    return null;
  }

  const iconTooltipTitle = hasNote ? `${label}: ${persisted}` : emptyLabel;

  const iconTrigger = iconLabel ? (
    <Tooltip title={iconTooltipTitle} arrow placement="top">
      <span>
        <Button
          type="button"
          size="small"
          disabled={triggerDisabled && canInteract}
          onClick={openEditor}
          onPointerDown={(event) => event.stopPropagation()}
          startIcon={<NoteAltOutlinedIcon sx={{ fontSize: 16 }} />}
          aria-label={hasNote ? `View ${label.toLowerCase()}` : emptyLabel}
          sx={{
            textTransform: 'none',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '0.78rem',
            lineHeight: 1.2,
            minHeight: 34,
            px: 1.1,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: hasNote ? '#7ec8e4' : '#E5E7EB',
            bgcolor: hasNote ? '#E8F4FF' : '#ffffff',
            color: hasNote ? '#0f7fa4' : '#4A5565',
            boxShadow: 'none',
            '&:hover': {
              borderColor: hasNote ? '#20AEDC' : '#d0d5dd',
              bgcolor: hasNote ? '#dff1fb' : '#f8fafc',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              borderColor: '#e4e7ec',
              color: '#98a2b3',
              bgcolor: '#f9fafb',
            },
            '& .MuiButton-startIcon': { mr: 0.55 },
          }}
        >
          {iconLabel}
        </Button>
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={iconTooltipTitle} arrow placement="top">
      <span>
        <IconButton
          type="button"
          size="small"
          disabled={triggerDisabled && canInteract}
          onClick={openEditor}
          onPointerDown={(event) => event.stopPropagation()}
          aria-label={hasNote ? `View ${label.toLowerCase()}` : emptyLabel}
          sx={{
            p: 0.25,
            flexShrink: 0,
            color: isOnDark
              ? hasNote
                ? '#ffffff'
                : 'rgba(255, 255, 255, 0.92)'
              : hasNote
                ? '#2CA7D6'
                : '#667085',
            bgcolor: isOnDark
              ? hasNote
                ? 'rgba(255, 255, 255, 0.18)'
                : 'transparent'
              : hasNote
                ? 'rgba(44, 167, 214, 0.1)'
                : 'transparent',
            '&:hover': {
              bgcolor: isOnDark ? 'rgba(255, 255, 255, 0.22)' : 'rgba(44, 167, 214, 0.12)',
            },
            '&.Mui-disabled': {
              color: isOnDark ? 'rgba(255, 255, 255, 0.45)' : '#98a2b3',
            },
          }}
        >
          <NoteAltOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </span>
    </Tooltip>
  );

  const emptyState = (
    <Box
      component={canInteract ? 'button' : 'div'}
      type={canInteract ? 'button' : undefined}
      onClick={canInteract && !disabled ? openEditor : undefined}
      disabled={canInteract ? disabled : undefined}
      aria-label={emptyLabel}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'flex-start',
        gap: isSm ? 1 : 1.15,
        width: '100%',
        maxWidth: '100%',
        cursor: canInteract ? 'pointer' : 'default',
        borderRadius: '12px',
        border: '1.5px dashed #b8c9d6',
        bgcolor: '#f8fbfd',
        px: isSm ? 1.15 : 1.35,
        py: isSm ? 1 : 1.2,
        textAlign: 'left',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': canInteract
          ? {
              borderColor: '#2CA7D6',
              bgcolor: '#eef7fb',
            }
          : undefined,
        '&:focus-visible': {
          outline: '2px solid #2CA7D6',
          outlineOffset: 2,
        },
        '&:disabled': {
          opacity: 0.55,
          cursor: 'not-allowed',
        },
      }}
    >
      <Box
        sx={{
          width: isSm ? 32 : 36,
          height: isSm ? 32 : 36,
          borderRadius: '10px',
          bgcolor: '#e6f6fc',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        <NoteAltOutlinedIcon sx={{ fontSize: isSm ? 18 : 20, color: '#2CA7D6' }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: isSm ? '0.8125rem' : '0.875rem',
            fontWeight: 700,
            color: '#1f4f63',
            lineHeight: 1.3,
          }}
        >
          {emptyLabel}
        </Typography>
        {helperText ? (
          <Typography
            sx={{
              mt: 0.35,
              fontFamily: 'Inter, sans-serif',
              fontSize: isSm ? '0.72rem' : '0.75rem',
              fontWeight: 500,
              color: '#667085',
              lineHeight: 1.4,
            }}
          >
            {helperText}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );

  const filledState = (
    <Box
      sx={{
        width: '100%',
        borderRadius: '12px',
        border: '1px solid #d7e6ef',
        bgcolor: '#f5f9fc',
        px: isSm ? 1.15 : 1.35,
        py: isSm ? 1 : 1.15,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 3,
            alignSelf: 'stretch',
            minHeight: isSm ? 36 : 42,
            borderRadius: 99,
            bgcolor: '#7fd0ef',
            flexShrink: 0,
            mt: 0.2,
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#5b7a8a',
              mb: 0.4,
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: isSm ? '0.8125rem' : '0.875rem',
              fontWeight: 500,
              color: '#1f2937',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {persisted}
          </Typography>
        </Box>
      </Box>

      {canInteract ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mt: 1,
            pt: 0.85,
            borderTop: '1px solid #e4eef4',
          }}
        >
          <Button
            type="button"
            size="small"
            disabled={disabled || isSaving}
            onClick={openEditor}
            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '0.78rem',
              color: '#2CA7D6',
              minHeight: 32,
              px: 1,
              borderRadius: '8px',
              '&:hover': { bgcolor: 'rgba(44, 167, 214, 0.08)' },
            }}
          >
            Edit note
          </Button>
          <Button
            type="button"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              void handleRemove();
            }}
            sx={{
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.78rem',
              color: '#b42318',
              minHeight: 32,
              px: 1,
              borderRadius: '8px',
              '&:hover': { bgcolor: 'rgba(180, 35, 24, 0.06)' },
            }}
          >
            Remove note
          </Button>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box
      sx={{ width: isIcon ? 'auto' : '100%', minWidth: 0, display: isIcon ? 'inline-flex' : 'block' }}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {isIcon ? iconTrigger : hasNote ? filledState : emptyState}

      <Dialog
        open={isOpen}
        onClose={closeEditor}
        fullWidth
        maxWidth="sm"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onKeyUp={(event) => event.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: '14px',
            border: '1px solid #e4e7ec',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.14)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1.05rem',
            color: '#1f2937',
            pb: 0.5,
          }}
        >
          {sessionMode === 'view'
            ? label
            : sessionMode === 'edit'
              ? `Edit ${label.toLowerCase()}`
              : emptyLabel}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {sessionMode !== 'view' ? (
            <>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.8125rem',
                  color: '#667085',
                  lineHeight: 1.45,
                  mb: 1.5,
                }}
              >
                {helperText}
                {sessionMode === 'edit'
                  ? ' Change the text below, then tap Save note.'
                  : ' Type below, then tap Save note.'}
              </Typography>
              <TextField
                inputRef={inputRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value.slice(0, MEAL_PLAN_NOTES_MAX_LENGTH))}
                onKeyDown={(event) => event.stopPropagation()}
                onKeyUp={(event) => event.stopPropagation()}
                placeholder={placeholder}
                multiline
                minRows={5}
                maxRows={12}
                disabled={isSaving}
                fullWidth
                error={overLimit}
                helperText={
                  overLimit
                    ? `Please keep your note under ${MEAL_PLAN_NOTES_MAX_LENGTH} characters.`
                    : `${draft.length} of ${MEAL_PLAN_NOTES_MAX_LENGTH} characters used`
                }
                FormHelperTextProps={{
                  sx: {
                    mx: 0,
                    mt: 0.75,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.72rem',
                    color: overLimit ? '#d92d20' : '#98a2b3',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                    bgcolor: '#f8fafc',
                    borderRadius: '12px',
                  },
                }}
              />
            </>
          ) : (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.9375rem',
                color: '#1f2937',
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {persisted}
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 0.5,
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: sessionMode === 'edit' && sessionHadNote ? 'space-between' : 'flex-end',
          }}
        >
          {sessionMode === 'edit' && sessionHadNote ? (
            <Button
              type="button"
              disabled={isSaving}
              onClick={() => void handleRemove()}
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#b42318',
                mr: 'auto',
              }}
            >
              Remove note
            </Button>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              disabled={isSaving}
              onClick={closeEditor}
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#475467',
                minHeight: 40,
                px: 1.5,
              }}
            >
              {sessionMode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {sessionMode !== 'view' ? (
              <Button
                type="button"
                variant="contained"
                disabled={isSaving || !isDirty || overLimit}
                onClick={() => void handleSave()}
                startIcon={
                  isSaving ? <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} /> : undefined
                }
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  minHeight: 40,
                  px: 2,
                  borderRadius: '10px',
                  bgcolor: '#20AEDC',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
                }}
              >
                {isSaving ? 'Saving…' : 'Save note'}
              </Button>
            ) : null}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
